/**
 * Sentry Error Monitoring
 * 
 * Initializes Sentry for error tracking in production.
 * Only loads if VITE_SENTRY_DSN is configured.
 */

let sentryInitialized = false;

export async function initSentry(): Promise<void> {
    const dsn = import.meta.env.VITE_SENTRY_DSN;

    if (!dsn) {
        if (import.meta.env.DEV) {
            console.info('[Sentry] DSN not configured, error tracking disabled');
        }
        return;
    }

    if (sentryInitialized) {
        return;
    }

    try {
        // Dynamic import to keep Sentry out of initial bundle
        const Sentry = await import('@sentry/react');

        Sentry.init({
            dsn,
            environment: import.meta.env.MODE,

            // Performance monitoring sample rate (adjust based on traffic)
            tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

            // Only send errors in production
            enabled: import.meta.env.PROD,

            // Filter out noisy errors
            ignoreErrors: [
                // Browser extensions
                /chrome-extension/,
                /moz-extension/,
                // Network errors (handled by app)
                'Failed to fetch',
                'NetworkError',
                // User-initiated navigation
                'AbortError',
            ],
        });

        sentryInitialized = true;
        console.info('[Sentry] Initialized successfully');
    } catch (error) {
        // Sentry init failure should not crash the app
        console.warn('[Sentry] Failed to initialize:', error);
    }
}

/**
 * Capture an error manually (for caught exceptions).
 */
export async function captureError(error: Error, context?: Record<string, unknown>): Promise<void> {
    if (!import.meta.env.VITE_SENTRY_DSN) {
        console.error('[Error]', error, context);
        return;
    }

    try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(error, { extra: context });
    } catch {
        console.error('[Error]', error, context);
    }
}

/**
 * Test Sentry integration.
 * 
 * In development: Call window.__testSentry() in console
 * In deployed preview/production: Check browser console for "[Sentry] Initialized" message
 * 
 * Also accessible programmatically via: testSentry()
 */
export async function testSentry(): Promise<void> {
    console.log('[Sentry] Sending test error...');

    try {
        const Sentry = await import('@sentry/react');
        Sentry.captureMessage('Sentry test message from HumanReplica.ai', 'info');
        Sentry.captureException(new Error('Sentry test error from HumanReplica.ai'));
        console.log('[Sentry] Test events sent! Check your Sentry dashboard.');
    } catch (error) {
        console.error('[Sentry] Test failed:', error);
    }
}

// Expose test function to window in development for easy console access
if (import.meta.env.DEV) {
    // @ts-expect-error - Adding dev-only global
    window.__testSentry = testSentry;
}
